import { Font } from "opentype.js";
import { renderToStaticMarkup } from "preact-render-to-string";
import { PapyrusNode } from "../types";

let domImpl: any = undefined;
let fetchImpl: ((input: RequestInfo | URL, init?: RequestInit) => Promise<Response>) | undefined = undefined;

/**
 * We use this to register a window in environments where one wouldn't be available.
 */
export function registerWindowImplementation(impl: any) {
    domImpl = impl;
}

export function registerFetchImplementation(impl: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>) {
    fetchImpl = impl;
}

function getWindow(): Window {
    if (!domImpl) {
        console.log(
            "DOM constructor is not registered. Assuming browser environment. If not, please register DOM constructor before calling this function.",
        );
        return window;
    }
    return new domImpl().window;
}

export function fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (!fetchImpl) {
        if (typeof window !== "undefined" && window.fetch) {
            return window.fetch(input, init);
        }
        if (typeof global !== "undefined" && global.fetch) {
            return global.fetch(input, init);
        }
        throw new Error("Fetch implementation is not registered. Please register it before calling this function.");
    }
    return fetchImpl(input, init);
}

const arrayBufferMimeTypeCache: Map<string, string> = new Map();
const arrayBufferFileCache: Map<string, Promise<ArrayBuffer>> = new Map();
const textFileCache: Map<string, Promise<string>> = new Map();

const getFromArrayBufferCache = async (url: string): Promise<ArrayBuffer> => {
    const cachedPromise = arrayBufferFileCache.get(url);
    if (cachedPromise) return cachedPromise;
    const getData = async () => {
        const resp = await fetch(url);
        arrayBufferMimeTypeCache.set(url, resp.headers?.get("content-type") || "");
        return await resp.arrayBuffer();
    };
    const ab = getData();
    arrayBufferFileCache.set(url, ab);
    return ab;
};

const getFromTextCache = async (url: string): Promise<string> => {
    const cachedPromise = textFileCache.get(url);
    if (cachedPromise) return cachedPromise;
    const getData = async () => {
        const resp = await fetch(url);
        return await resp.text();
    };
    const ab = getData();
    textFileCache.set(url, ab);
    return ab;
};

// Whether each capability in the list is present.
// The Web API has all sorts of stuff that aren't present on the server.
const hasCapabilities = (capabilities: string[]): boolean => {
    return capabilities.every((cap) => cap !== "undefined");
};

export const createElement = (tag: string): HTMLElement => {
    if (hasCapabilities([typeof document])) {
        return document.createElement(tag);
    }
    const doc = getWindow().document;
    return doc.createElement(tag);
};

export const createElementNS = (namespace: string, tag: string) => {
    if (hasCapabilities([typeof document])) {
        return document.createElementNS(namespace, tag);
    }
    const doc = getWindow().document;
    const elem = doc.createElement(tag);
    elem.setAttribute("xmlns", namespace);
    return elem;
};

export const domParser = () => {
    if (hasCapabilities([typeof DOMParser])) {
        return new DOMParser();
    }
    const w = getWindow();
    return new (w as any).DOMParser();
};

export const domParserConstructor = (): typeof DOMParser => {
    if (hasCapabilities([typeof DOMParser])) {
        return DOMParser;
    }
    const w = getWindow();
    return (w as any).DOMParser;
};

export const fetchAsArrayBuffer = (externalUrl: string, allowCache?: boolean): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        if (!allowCache) {
            fetch(externalUrl)
                .then((response) => {
                    response.arrayBuffer().then(resolve).catch(reject);
                })
                .catch((err) => {
                    reject(err);
                });
        } else {
            getFromArrayBufferCache(externalUrl).then(resolve).catch(reject);
        }
    });
};

export const fetchAsString = (externalUrl: string, allowCache?: boolean): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (!allowCache) {
            fetch(externalUrl)
                .then((response) => {
                    response.text().then(resolve).catch(reject);
                })
                .catch(reject);
        } else {
            getFromTextCache(externalUrl).then(resolve).catch(reject);
        }
    });
};

export const renderPapyrusComponentAsString = (component: PapyrusNode) => {
    const svg = renderToStaticMarkup(component as any);
    return svg;
};

/**
 * Cache for fontfaces to prevent multiple loads of the same font.
 * This is required since the browser will still attempt to load it again every time, even if it would hit the cache.
 * Spawning off many requests for items that would hit cache still takes time, and can actually get blocked by the browser.
 */
const fontFaceCache: Map<string, Promise<FontFace>> = new Map();

/**
 * Loads a given font as a font face set in the browser. This function has
 * no effect in a server environment, in the browser it gives the browser access to a font face for
 * use in font rendering.
 * If the font has already been loaded, this function will return the existing promise.
 * @param font An opentype font object.
 * @param externalUrl An external URL that can be used to access the original font file.
 * @returns A promise resolved when the font has been loaded. Rejected if an error occured.
 */
export const loadFontFaceSet = async (font: Font, externalUrl: string): Promise<FontFace | undefined> => {
    if (hasCapabilities([typeof FontFace])) {
        if (fontFaceCache.has(externalUrl)) {
            return fontFaceCache.get(externalUrl);
        }
        const familyName = font.names.fullName["en"];
        const fontFace = new FontFace(familyName, `url(${externalUrl})`);
        (document.fonts as any).add(fontFace);
        const promise = fontFace.load();
        fontFaceCache.set(externalUrl, promise);
        return promise;
    }
    return undefined;
};

export const xmlSerializer = (): XMLSerializer => {
    if (hasCapabilities([typeof XMLSerializer])) {
        return new XMLSerializer();
    }
    const w = getWindow();
    return new (w as any).XMLSerializer();
};

export const toBase64 = (input: string): string => {
    return btoa(input);
};

// The file-type library guesses an array buffer's mime type using magic numbers.
// It therefore only works on binary formats, not text formats like SVG.
// SVG is currently the only text case that this function must handle.
// If that ever changes then this function will have to become smarter.
export const getMimeTypeOfArrayBuffer = (arrayBuffer: ArrayBuffer): string => {
    function getMimeTypeFromArrayBuffer() {
        const uint8arr = new Uint8Array(arrayBuffer);
        const len = 4;
        if (uint8arr.length >= len) {
            const signatureArr = new Array(len);
            for (let i = 0; i < len; i++) signatureArr[i] = new Uint8Array(arrayBuffer)[i].toString(16);
            const signature = signatureArr.join("").toUpperCase();

            switch (signature) {
                case "89504E47":
                    return "image/png";
                case "47494638":
                    return "image/gif";
                case "25504446":
                    return "application/pdf";
                case "504B0304":
                    return "application/zip";
            }

            // Some magic numbers are shorter.
            const sig = signature.slice(0, 4);
            switch (sig) {
                case "FFD8":
                    return "image/jpeg";
            }
        }
        return null;
    }
    const mimeTypeData = getMimeTypeFromArrayBuffer();
    if (!mimeTypeData) {
        return "image/svg+xml";
    }
    return mimeTypeData;
};

export const dataUrlFromExternalUrl = async (url: string): Promise<string> => {
    const blob = await getFromArrayBufferCache(url);
    const type = arrayBufferMimeTypeCache.get(url) || getMimeTypeOfArrayBuffer(blob);
    return await webBlobToDataUrl(new Blob([blob], { type }));
};

const uint8ArrayToString = (array: Uint8Array): string => {
    let binary = "";
    const len = array.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(array[i]);
    }
    return binary;
};

export const arrayBufferToDataUrl = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    const mimeType = getMimeTypeOfArrayBuffer(arrayBuffer);

    const uint8Array = new Uint8Array(arrayBuffer);
    const binaryString = uint8ArrayToString(uint8Array);
    const b64 = btoa(binaryString);

    return `data:${mimeType};base64,${b64}`;
};

export const svgToDataUrl = (svgString: string): string => {
    return `data:image/svg+xml;base64,${btoa(svgString)}`;
};

export const dataUrlToArrayBuffer = (dataUrl: string): ArrayBuffer => {
    // strip newlines
    const uri = dataUrl.replace(/\r?\n/g, "");

    // split the URI up into the "metadata" and the "data" portions
    const firstComma = uri.indexOf(",");
    if (firstComma === -1 || firstComma <= 4) {
        throw new TypeError("malformed data: URI");
    }

    // remove the "data:" scheme and parse the metadata
    const meta = uri.substring(5, firstComma).split(";");

    let base64 = false;
    for (let i = 1; i < meta.length; i++) {
        if (meta[i] === "base64") {
            base64 = true;
        }
    }
    // get the encoded data portion and decode URI-encoded chars
    const data = decodeURI(uri.substring(firstComma + 1));

    // convert the data to an array buffer.
    if (base64) {
        const buf = new Uint8Array(
            atob(data)
                .split("")
                .map((c) => c.charCodeAt(0)),
        );
        return buf.buffer;
    } else {
        const buf = new Uint8Array(data.split("").map((c) => c.charCodeAt(0)));
        return buf.buffer;
    }
};

const webBlobToDataUrl = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const a = new FileReader();
        a.onload = function (e: any) {
            const target = e.target;
            if (!target || !target.result) {
                reject();
            } else {
                resolve(target.result.toString());
            }
        };
        a.readAsDataURL(blob);
    });
};
